<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { formatPhone } from "@/helpers/phone";
import { ContactActiveProposalStatus, EContactType } from '@/types/contact';

import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

import { SynchronizeFieldType } from "../types";

interface Props {
    label: string;
    value?: string | number;
    formValue?: string | number;
    icon?: string;
    type?: SynchronizeFieldType;
}

const props = withDefaults(defineProps<Props>(), {
    type: SynchronizeFieldType.OTHER,
});

// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const emit = defineEmits<(e: "click") => void>();

const { t } = useI18n();

const activeProposalStatusOptions = [
    { label: t("page.client.contact.activeProposalStatusOption.notConcerned"), value: ContactActiveProposalStatus.NOT_CONCERNED },
    { label: t("page.client.contact.activeProposalStatusOption.concerned"), value: ContactActiveProposalStatus.CONCERNED },
    { label: t("page.client.contact.activeProposalStatusOption.noReceive"), value: ContactActiveProposalStatus.NO_RECEIVE },
];

const roleOptions = [
    { label: t("page.client.contact.rolesOption.decisionMaker"), value: EContactType.DECISION_MAKER },
    { label: t("page.client.contact.rolesOption.prescriber"), value: EContactType.PRESCRIBER },
    { label: t("page.client.contact.rolesOption.hr"), value: EContactType.HR },
    { label: t("page.client.contact.rolesOption.accountant"), value: EContactType.ACCOUNTANT },
    { label: t("page.client.contact.rolesOption.qhse"), value: EContactType.QHSE },
    { label: t("page.client.contact.rolesOption.uniqueContact"), value: EContactType.UNIQUE_CONTACT },
];

const displayValue = computed<string | undefined>(() => {
    if (!props.value) return "";
    switch (props.type) {
        case SynchronizeFieldType.ACTIVE_PROPOSAL_STATUS:
            return activeProposalStatusOptions.find((option) => option.value === props.value)?.label;
        case SynchronizeFieldType.ROLE:
            return roleOptions.find((option) => option.value === props.value)?.label;
        case SynchronizeFieldType.PHONE:
            return formatPhone(props.value);
        default:
            return String(props.value);
    }
});

const cleanedString = (value: string | number | undefined) => {
    if (!value) return "";
    return String(value)
        .trim()
        .toLowerCase()
        .replace(/\s|\.|-/g, "");
};

const compareValue = computed<boolean>(() => {
    return cleanedString(props.formValue) === cleanedString(props.value);
});

const iconClasses = computed<string>(() => {
    if (compareValue) return "ti red ti-circle-x-filled";
    return "ti green ti-circle-check-filled";
});
</script>
<template>
    <div v-if="displayValue" class="synchronize-field">
        <div class="label-container">
            <div class="label">{{ label }}</div>
            <div v-if="formValue" class="icon">
                <i v-if="!compareValue" :class="iconClasses"></i>
            </div>
        </div>
        <div class="value-container">
            <TextInputAlt :name="label" :modelValue="displayValue" :icon-left="icon" variant="grey" state="filled" readonly />
        </div>
    </div>
</template>

<style scoped lang="scss">
.synchronize-field {
    display: flex;
    align-items: center;
    height: 28px;
}
.label-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-xs, 4px);
    flex-shrink: 0;
    width: 150px;
}
.value-container {
    display: flex;
    align-items: center;
}
</style>
